import config from 'constants/config'
import noop from 'lib/function/noop'

export const isAbortError = (error: unknown): error is DOMException =>
  error instanceof DOMException && error.name === 'AbortError'

function createTimeoutReason(): unknown {
  try {
    // Match AbortSignal.timeout() semantics (TimeoutError DOMException).
    return new DOMException('The operation timed out.', 'TimeoutError')
  } catch(error) {
    noop(error)
  }

  const fallback = new Error('The operation timed out.')
  ;(fallback as Error & { name: string }).name = 'TimeoutError'
  return fallback
}

// TODO: remove this function and use the native AbortSignal.timeout() function when we drop support for Safari 16 and older
function createTimeoutSignal(
  timeoutMs: number,
  upstreamSignal?: AbortSignal | null,
): { signal: AbortSignal, cleanup: () => void } {
  const canUseNativeTimeout =
    typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
  const canUseAny =
    typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function'

  if (canUseNativeTimeout) {
    const timeoutSignal = AbortSignal.timeout(timeoutMs)

    if (!upstreamSignal) {
      return { signal: timeoutSignal, cleanup: noop }
    }

    if (canUseAny) {
      return { signal: AbortSignal.any([upstreamSignal, timeoutSignal]), cleanup: noop }
    }

    const controller = new AbortController()
    const cleanupFns: Array<() => void> = []

    const forwardAbort = (signal: AbortSignal) => {
      const onAbort = () =>
        controller.abort((signal as AbortSignal & { reason?: unknown }).reason)

      if (signal.aborted) {
        onAbort()
        return
      }

      signal.addEventListener('abort', onAbort, { once: true })
      cleanupFns.push(() => signal.removeEventListener('abort', onAbort))
    }

    forwardAbort(upstreamSignal)
    forwardAbort(timeoutSignal)

    return { signal: controller.signal, cleanup: () => cleanupFns.forEach((fn) => fn()) }
  }

  const controller = new AbortController()
  const timeoutReason = createTimeoutReason()

  let timeoutId: ReturnType<typeof setTimeout> | null = null
  const cleanupFns: Array<() => void> = []

  // If the caller provided their own signal, preserve it by forwarding aborts.
  if (upstreamSignal) {
    if (upstreamSignal.aborted) {
      controller.abort((upstreamSignal as AbortSignal & { reason?: unknown }).reason)
    } else {
      const onAbort = () => controller.abort((upstreamSignal as AbortSignal & { reason?: unknown }).reason)
      upstreamSignal.addEventListener('abort', onAbort, { once: true })
      cleanupFns.push(() => upstreamSignal.removeEventListener('abort', onAbort))
    }
  }

  timeoutId = setTimeout(() => controller.abort(timeoutReason), timeoutMs)
  cleanupFns.push(() => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  })

  return {
    signal: controller.signal,
    cleanup: () => cleanupFns.forEach((fn) => fn()),
  }
}

// we json serialise body, so allow anything
export type ObjectOptions = Omit<RequestInit, 'body'> & {
  body?: any
  timeout?: number
  /**
   * Will not automatically add the brand to the query/body
   */
  excludeBrand?: boolean
}
export type ObjectOptionsWithMethod = ObjectOptions & { method: string, signal?: AbortSignal | null }
export interface ProcessorProps {
  endpoint: string
  path: string
  options: ObjectOptionsWithMethod
}
export type PreProcessor = (props: ProcessorProps) => ProcessorProps
export type Interceptor = (promise: Promise<any>, response: Response) => Promise<Response>

const preProcessors: Array<PreProcessor> = []
const interceptors: Array<Interceptor> = []

export function registerRequestPreprocessor(preProcessor: PreProcessor) {
  preProcessors.push(preProcessor)
}

export function registerRequestInterceptor(interceptor: Interceptor) {
  interceptors.push(interceptor)
}

export function unregisterRequestPreprocessor(preProcessor: PreProcessor) {
  const index = preProcessors.indexOf(preProcessor)
  if (index > 0) {
    preProcessors.splice(index, 1)
  }
}

export function unregisterRequestInterceptor(interceptor: Interceptor) {
  const index = interceptors.indexOf(interceptor)
  if (index > 0) {
    interceptors.splice(index, 1)
  }
}

export function resetRequestMiddlewares() {
  preProcessors.splice(0, preProcessors.length)
  interceptors.splice(0, interceptors.length)
}

function sendFetchRequest(
  endpoint: string,
  options: ObjectOptions,
  responseResolver: (value: Response | PromiseLike<Response>) => void,
): Promise<any> {
  return fetch(endpoint, options)
    .then((response) => {
      responseResolver(response)
      const status = response.status
      if (status >= 400) {
        throw response
      }

      const contentType = response.headers?.get('Content-Type')
      if (status === 204 || !contentType) {
        return Promise.resolve()
      }

      if (contentType.includes('application/json')) {
        return response.json()
      }

      if (contentType.includes('application/pdf')) {
        return response.blob()
      }

      if (contentType.includes('text/csv') || contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || contentType.includes('application/vnd.ms-excel')) {
        return response.blob()
      }

      return response.text()
    })
    .catch((response) => {
      responseResolver(response)
      if (isAbortError(response)) {
        return
      }
      throw response
    })
}

function sendXMLHttpRequest(
  endpoint: string,
  options: ObjectOptionsWithMethod,
  responseResolver: (value: unknown) => void,
  onProgress?: (percentage: number) => void,
): Promise<any> {
  const xmlRequest = new XMLHttpRequest()
  if (typeof options.timeout === 'number' && Number.isFinite(options.timeout)) {
    xmlRequest.timeout = options.timeout
  }

  // upload progress event
  xmlRequest.upload.addEventListener('progress', function(e) {
    // upload progress as percentage
    const percent_completed = (e.loaded / e.total) * 100
    onProgress?.(percent_completed)
  })

  const { resolve, reject, promise } = Promise.withResolvers<void>()

  // request successfully finished event
  xmlRequest.addEventListener('load', function() {
    resolve()
  })
  // request failed event
  xmlRequest.addEventListener('error', function() {
    reject()
  })
  // request aborted event
  xmlRequest.addEventListener('abort', function() {
    reject('The operation was aborted!')
  })
  options.signal?.addEventListener('abort', function() {
    xmlRequest.abort()
  })
  // request timed out event
  xmlRequest.addEventListener('timeout', function() {
    reject('The operation timed out!')
  })

  xmlRequest.open(options.method, endpoint)

  // Set withCredentials if credentials option is 'include'
  if (options.credentials === 'include') {
    xmlRequest.withCredentials = true
  }

  if (options.headers) {
    Object.entries(options.headers).forEach(([key, value]) => {
      xmlRequest.setRequestHeader(key, value)
    })
  }

  xmlRequest.send(options.body)

  return promise
    .then(() => {
      responseResolver(xmlRequest)

      const status = xmlRequest.status
      if (status >= 400) {
        throw xmlRequest.response
      }

      const contentType = xmlRequest.getAllResponseHeaders()
      if (status === 204 || !contentType) {
        return Promise.resolve()
      }

      // TODO improve for different response type
      // when needed
      if (contentType.includes('application/json')) {
        return JSON.parse(xmlRequest.response)
      }

      return xmlRequest.response
    })
    .catch((response) => {
      responseResolver(response)

      if (isAbortError(response)) {
        return
      }

      let error = response
      if (typeof response === 'string') {
        try {
          error = JSON.parse(response)
        } catch(error) {
          noop(error)
        }
      }
      throw error
    })
}

const getDefaultEndpoint = () => {
  if (!IS_SSR) {
    // If it's not server side rendering, then return the public API endpoint with config.SCHEME
    return `${config.SCHEME}://${config.API_HOST}`
  }

  const serverSideAPIHost = config.SERVER_SIDE_API_HOST ?? config.API_HOST

  // If server side API host starting with http(s), then return it without adding schema.
  if (serverSideAPIHost.startsWith('http')) {
    return serverSideAPIHost
  }

  // If server side API host is not starting with http, then return it with config.SCHEME
  return `${config.SCHEME}://${serverSideAPIHost}`
}

const defaultEndpoint = getDefaultEndpoint()
/**
 * Does the fetch
 *
 * Lifecycle is:
 * -- Preprocessors
 * Stringify body to JSON (if required)
 * Fetch request sent
 * Parse Result
 * Parse Error (if required)
 * -- Interceptors
 */
async function request<T>(
  path: string,
  options: ObjectOptionsWithMethod,
  endpointOverride?: string,
  onProgress?: (percentage: number) => void,
): Promise<T> {
  const endpoint = endpointOverride ?? defaultEndpoint
  const processedParams = preProcessors.reduce((acc, processor) => processor(acc), {
    path,
    options,
    endpoint,
  })

  let cleanupTimeout = noop

  let isFormData = false
  if (typeof processedParams.options?.body === 'object') {
    isFormData = processedParams.options.body instanceof FormData
    if (!isFormData) {
      processedParams.options.headers = {
        ...processedParams.options.headers,
        'Content-Type': 'application/json',
      }
      processedParams.options.body = JSON.stringify(processedParams.options.body)
    }
  }

  // `AbortSignal.timeout()` isn't available in older iOS Safari (e.g. 15.x).
  // Implement timeouts via AbortController so we can support these browsers.
  if (!isFormData && typeof processedParams.options.timeout === 'number' && Number.isFinite(processedParams.options.timeout)) {
    const { signal, cleanup } = createTimeoutSignal(
      processedParams.options.timeout,
      processedParams.options.signal,
    )
    processedParams.options.signal = signal
    cleanupTimeout = cleanup
  }

  // because we mutate interceptors as we add/merge them, there's a chance the set of interceptors
  // is different from before the request to after the request - that's not really what we want
  // so 'snapshot' the set of current interceptors just before we made the request
  const snapshotInterceptors = [...interceptors]
  const { resolve, promise: pendingResponse } = Promise.withResolvers<Response>()

  try {
    const requestProvider = isFormData ?
        sendXMLHttpRequest(`${endpoint}${processedParams.path}`, processedParams.options, resolve, onProgress) :
        sendFetchRequest(`${endpoint}${processedParams.path}`, processedParams.options, resolve)

    const fetchPromise = requestProvider
      .catch(async(response) => {
        if (response instanceof Response) {
          let error
          if (response.headers?.get('Content-Type')?.includes('application/json')) {
            error = await response.json()
          } else {
            error = {
              message: await response.text() ?? 'Something went wrong',
              status: response.status ?? 500,
            }
          }
          // oxlint-disable-next-line no-throw-literal -- re-throws parsed API error payload; app-wide contract expects structured objects, not Error instances
          throw error
        }

        // non-response error, no need to parse - pass it along
        throw response
      })
    const rawResponse = await pendingResponse
    return await snapshotInterceptors.reduce((promise, interceptor) => interceptor(promise, rawResponse), fetchPromise)
  } finally {
    cleanupTimeout()
  }
}

function getRequest<T>(
  path: string,
  options: ObjectOptions = {},
  endpointOverride?: string,
) {
  return request<T>(path, {
    ...options,
    method: 'GET',
  }, endpointOverride)
}

function postRequest<Result, RequestData>(
  path: string,
  data: RequestData,
  options: ObjectOptions = {},
  endpointOverride?: string,
) {
  return request<Result>(path, {
    ...options,
    method: 'POST',
    body: data,
  }, endpointOverride)
}

function patchRequest<T, D>(
  path: string,
  data: D,
  options: ObjectOptions = {},
  endpointOverride?: string,
) {
  return request<T>(path, {
    ...options,
    method: 'PATCH',
    body: data,
  }, endpointOverride)
}

function putRequest<T, D>(
  path: string,
  data: D,
  options: ObjectOptions = {},
  endpointOverride?: string,
) {
  return request<T>(path, {
    ...options,
    method: 'PUT',
    body: data,
  }, endpointOverride)
}

function deleteRequest<T>(
  path: string,
  options: ObjectOptions = {},
  endpointOverride?: string,
) {
  return request<T>(path, {
    ...options,
    method: 'DELETE',
  }, endpointOverride)
}

function optionsRequest<T>(
  path: string,
  options: ObjectOptions = {},
  endpointOverride?: string,
) {
  return request<T>(path, {
    ...options,
    method: 'OPTIONS',
  }, endpointOverride)
}

interface UploadProps {
  path: string
  data: FormData
  options: Omit<RequestInit, 'body'>
  endpointOverride?: string
}
/**
 * Uploads a FormData returning the progress
 */
function uploadRequest<T>(
  props: UploadProps,
  onProgress?: (percentage: number) => void,
) {
  return request<T>(
    props.path,
    {
      method: 'POST',
      ...(props.options ?? {}),
      body: props.data,
    },
    props.endpointOverride,
    onProgress,
  )
}

export default {
  get: getRequest,
  post: postRequest,
  patch: patchRequest,
  put: putRequest,
  delete: deleteRequest,
  options: optionsRequest,
  upload: uploadRequest,
}

export function authOptions(accessToken?: string): ObjectOptions {
  if (accessToken) {
    return { headers: { Authorization: `Bearer ${accessToken}` } }
  } else {
    return { credentials: 'include' }
  }
}

const bodyMethods = new Set(['POST', 'PUT', 'PATCH'])
interface ParamToAppend {
  key: string
  value: string
}

export function appendParams(paramToAppend: ParamToAppend, params: ProcessorProps) {
  const { options } = params
  if (bodyMethods.has(options.method)) {
    const isFormData = options.body instanceof FormData
    if (isFormData) {
      options.body.append(paramToAppend.key, paramToAppend.value)
    } else {
      options.body = {
        ...options.body,
        [paramToAppend.key]: paramToAppend.value,
      }
    }
  } else {
    if (params.path.endsWith('?')) {
      params.path += `${paramToAppend.key}=${paramToAppend.value}`
    } else if (params.path.includes('?')) {
      params.path += `&${paramToAppend.key}=${paramToAppend.value}`
    } else {
      params.path += `?${paramToAppend.key}=${paramToAppend.value}`
    }
  }
}
